import { useCallback } from "react"
import { useSetState } from "ahooks-v2"
import { createModel } from "hox"
import type { MyAuthStrategyVO } from "@/api"
import { IndustryCategory, RoleAuthStrategy } from "@/api"
import { getTheme, compatibleFocusBlank } from "@/util/commonUtils"
import { isInvalid } from "@/util/util"
import { isNH } from "@/constants/NHConfig"
// ==============================================
const IS_ALL_SHOW = "IS_ALL_SHOW"
// ==============================================
interface stateVO {
  // 页面内容是否显示
  isAllShow: boolean
  // 主题颜色 dark,light
  theme: string
  // 用户权限
  userAuth?: MyAuthStrategyVO
  // 是否显示logo
  isShowLogo: boolean
  // 是否有多用户操作权限
  isHasOperateAuth: boolean
}
function useController() {
  const showValue = sessionStorage.getItem(IS_ALL_SHOW)
  const isAllShow = isInvalid(showValue)
    ? true
    : typeof JSON.parse(showValue as string) === "boolean"
    ? JSON.parse(showValue as string)
    : true
  const [state, set] = useSetState<stateVO>({
    isAllShow,
    theme: getTheme(),
    isShowLogo: false,
    isHasOperateAuth: false,
  })

  // 控制页面整体内容显隐
  const toggleAllShow = useCallback(
    (isShow: boolean) => {
      set({
        isAllShow: isShow,
      })
      sessionStorage.setItem(IS_ALL_SHOW, `${isShow}`)
    },
    [set],
  )

  // 控制主题颜色
  const toggleTheme = useCallback(
    (theme: string) => {
      set({
        theme,
      })
      // css
      document.documentElement.setAttribute("theme", theme)
      // local
      window.localStorage.setItem("theme", theme)
    },
    [set],
  )

  // 保存用户权限
  const setUserAuth = useCallback(
    (userAuth?: MyAuthStrategyVO) => {
      const industryCategory = userAuth?.industryCategory
      const roleAuths = userAuth?.roleAuths || []
      const isAuth = roleAuths.includes(RoleAuthStrategy.MULTI_USER_OPERATION)

      set({
        userAuth,
        // 是否显示logo
        isShowLogo: isNH
          ? false
          : industryCategory && industryCategory !== IndustryCategory.ADVERTISEMENT,
        // 是否有多用户操作权限
        isHasOperateAuth: !!(userAuth && isAuth),
      })
    },
    [set],
  )

  // 兼容H5输入框focus时底部出现空白
  const compatibleFocusFn = useCallback(compatibleFocusBlank, [])

  // 控制地图上鼠标样式
  const toggleMapMouseStyle = useCallback((className: string) => {
    const element = document.getElementById("view-default-view")
    if (element) {
      element.className = className
    }
  }, [])

  return {
    ...state,
    toggleAllShow,
    toggleTheme,
    setUserAuth,
    compatibleFocusFn,
    toggleMapMouseStyle,
  }
}

export default createModel(useController)
